import _ from "lodash";

const METRIC_KEYS = ["grossPremium", "brokerage", "trCommission", "profit", "policyCount"];

export const NO_TEAM_GROUP_ID = "__no_team__";

const emptyMetrics = () => ({
  grossPremium: 0,
  brokerage: 0,
  trCommission: 0,
  profit: 0,
  policyCount: 0,
});

const calcYoyPercent = (currentValue, previousValue) => {
  const cy = Number(currentValue) || 0;
  const ly = Number(previousValue) || 0;
  if (ly === 0) return null;
  return _.round(((cy - ly) / ly) * 100, 1);
};

const buildYoy = (cy, ly) =>
  METRIC_KEYS.reduce((acc, key) => {
    acc[key] = calcYoyPercent(cy[key], ly[key]);
    return acc;
  }, {});

const addChannelMetrics = (target, channel) => {
  METRIC_KEYS.forEach((key) => {
    target[key] += channel.ly?.[key] ?? 0;
    target.cy[key] += channel.cy?.[key] ?? 0;
  });
};

const finalizeGroup = (group) => ({
  ...group,
  yoy: buildYoy(group.cy, group.ly),
  members: [...group.members].sort(
    (a, b) => (b.cy?.grossPremium ?? 0) - (a.cy?.grossPremium ?? 0)
  ),
});

const createGroup = (groupId, name) => ({
  groupId,
  name,
  members: [],
  ly: emptyMetrics(),
  cy: emptyMetrics(),
});

export const groupChannelsByTeam = (channels = [], noTeamLabel = "No Team") => {
  const groups = new Map();

  channels.forEach((channel) => {
    const team = channel.teams?.[0];
    const groupId = team?.id || NO_TEAM_GROUP_ID;
    const name = team?.name || noTeamLabel;

    if (!groups.has(groupId)) {
      groups.set(groupId, createGroup(groupId, name));
    }

    const group = groups.get(groupId);
    group.members.push(channel);
    METRIC_KEYS.forEach((key) => {
      group.ly[key] += channel.ly?.[key] ?? 0;
      group.cy[key] += channel.cy?.[key] ?? 0;
    });
  });

  return [...groups.values()]
    .map(finalizeGroup)
    .sort((a, b) => b.cy.grossPremium - a.cy.grossPremium);
};

export const groupChannelsByTopLine = (channels = []) => {
  const groups = new Map();

  channels.forEach((channel) => {
    const topLine = channel.topLine || {};
    const groupId = topLine.id || channel.consultantId;
    const name = topLine.name || channel.name || groupId;

    if (!groups.has(groupId)) {
      groups.set(groupId, createGroup(groupId, name));
    }

    const group = groups.get(groupId);
    group.members.push(channel);
    METRIC_KEYS.forEach((key) => {
      group.ly[key] += channel.ly?.[key] ?? 0;
      group.cy[key] += channel.cy?.[key] ?? 0;
    });
  });

  return [...groups.values()]
    .map(finalizeGroup)
    .sort((a, b) => b.cy.grossPremium - a.cy.grossPremium);
};

export const buildFooterTotals = (channels = []) => {
  const ly = emptyMetrics();
  const cy = emptyMetrics();

  channels.forEach((channel) => {
    METRIC_KEYS.forEach((key) => {
      ly[key] += channel.ly?.[key] ?? 0;
      cy[key] += channel.cy?.[key] ?? 0;
    });
  });

  return {
    ly,
    cy,
    yoy: buildYoy(cy, ly),
  };
};
